import { SandboxEvent, SandboxStore } from '../../store';
import { SandboxOperationHandler } from '../types';
import { inputObject, page } from './utils';

const ACTIVE_WORKSHIFT_ID = 701;
const SANDBOX_DEPOSIT_SPEND = 4350;
const SANDBOX_ONLINE_DEPOSIT = 5360;
const SANDBOX_BONUS_PAYMENT = 360;

const eventView = (event: SandboxEvent): Record<string, unknown> => ({
  host: null,
  host2: null,
  role: null,
  role2: null,
  comment: null,
  payment: null,
  value1: null,
  value2: null,
  penalty: null,
  booking: null,
  hosts: [],
  tariff: null,
  cash_order: null,
  client_session: null,
  deposit: null,
  duration: null,
  combo: null,
  combo_items: [],
  discount: null,
  good: null,
  goods: [],
  host_group: null,
  new_devices: [],
  operator: null,
  cash_on_start: null,
  promo_campaign: null,
  promo_code_item: null,
  company_news_item: null,
  company_news_item_changes: null,
  service: null,
  setting: null,
  user_group: null,
  user_group2: null,
  worker_task: null,
  host_command: null,
  cashback: null,
  payment_items: [],
  payment_transaction: null,
  manager: null,
  warehouse_item: null,
  send_code_method: null,
  goods_category: null,
  advertisement: null,
  ...event,
});

export const workshift = (store: SandboxStore): Record<string, unknown> => {
  const payments = store.getState().payments;
  const paid = payments.filter(
    (payment) => !payment.is_refunded && payment.status === 'PAID'
  );
  const byType = (type: string): number =>
    paid.reduce(
      (sum, payment) =>
        sum +
        payment.items
          .filter((item) => item.type === type)
          .reduce((itemSum, item) => itemSum + item.sum, 0),
      0
    );
  return {
    id: ACTIVE_WORKSHIFT_ID,
    total: paid.reduce((sum, payment) => sum + payment.sum, 0),
    cash: paid.reduce((sum, payment) => sum + payment.cash_sum, 0),
    card: paid.reduce((sum, payment) => sum + payment.card_sum, 0),
    deposit:
      SANDBOX_DEPOSIT_SPEND +
      paid.reduce((sum, payment) => sum + payment.sums.deposit, 0),
    online_deposit: SANDBOX_ONLINE_DEPOSIT,
    bonus:
      SANDBOX_BONUS_PAYMENT +
      paid.reduce((sum, payment) => sum + payment.sums.bonus, 0),
    refunded: payments
      .filter((payment) => payment.is_refunded)
      .reduce((sum, payment) => sum + payment.sum, 0),
    cash_on_start: 5000,
    sum: {
      good: byType('GOOD') + byType('COMBO'),
      service: byType('SERVICE'),
      tariff: byType('TARIFF'),
      deposit: byType('DEPOSIT'),
      tips: 0,
    },
    created_at: new Date(store.getAnchorMs() - 4 * 3600000).toISOString(),
    finished_at: null,
    cash_orders: [],
    worker: store.getWorker(),
    payments,
  };
};

export const paymentResolvers: Record<string, SandboxOperationHandler> = {
  validatePayment: (variables, store) => ({
    validatePayment: store.validatePayment(inputObject(variables) as never),
  }),
  createPayment: (variables, store) => ({
    createPayment: store.createPayment(inputObject(variables) as never),
  }),
  validateRefund: (variables, store) => {
    const id = Number(inputObject(variables).id);
    const payment = store.getState().payments.find((item) => item.id === id);
    if (!payment || payment.is_refunded || payment.not_refundable)
      throw new Error('Платеж недоступен для возврата');
    return { validateRefund: true };
  },
  refundPayment: (variables, store) => ({
    refundPayment: store.refundPayment(Number(inputObject(variables).id)),
  }),
  ProcessPostPayment: (variables, store) => ({
    processPostPayment: store.processPostPayment(
      Number(variables.processPostPaymentId),
      inputObject(variables)
    ),
  }),
  activeWorkShift: (_, store) => ({ activeWorkShift: workshift(store) }),
  workshiftPayments: (_, store) => ({ activeWorkShift: workshift(store) }),
  getPostPaymentCount: (_, store) => ({ activeWorkShift: workshift(store) }),
  getWorkShiftPaymentOverviewData: (_, store) => ({
    getWorkShiftPaymentOverviewData: workshift(store),
  }),
  getPaymentsByClientId: (variables, store) => {
    const payments = store
      .getState()
      .payments.filter(
        (payment) => payment.client?.uuid === String(variables.uuid)
      )
      .map((payment) => ({
        ...payment,
        title: payment.items.some((item) => item.type === 'DEPOSIT')
          ? 'Пополнение депозита'
          : payment.items[0]?.entity?.title ?? 'Платеж',
        amount: payment.items.reduce((sum, item) => sum + item.amount, 0),
        paymentMethod: payment.method,
        worker: store.getWorker(),
      }));
    return { getPaymentsByClientId: page(payments, variables) };
  },
  EventList: (variables, store) => {
    const input = inputObject(variables);
    const allowedTypes = Array.isArray(input.types)
      ? input.types.map(String)
      : [];
    const query = String(input.q ?? '')
      .trim()
      .toLowerCase();
    const requestedWorkshiftId = input.work_shift_id;
    const hasWorkshiftFilter =
      requestedWorkshiftId !== undefined && requestedWorkshiftId !== null;
    const activeWorkshift = hasWorkshiftFilter ? workshift(store) : null;
    const workshiftMatches =
      !hasWorkshiftFilter ||
      Number(requestedWorkshiftId) === Number(activeWorkshift?.id);
    const requestedStart =
      input.start === undefined || input.start === null
        ? null
        : Date.parse(String(input.start));
    const requestedFinish =
      input.finish === undefined || input.finish === null
        ? null
        : Date.parse(String(input.finish));
    const workshiftStart = activeWorkshift
      ? Date.parse(String(activeWorkshift.created_at))
      : null;
    const workshiftFinish = activeWorkshift
      ? Date.parse(
          String(activeWorkshift.finished_at ?? store.getCurrentTimestamp())
        )
      : null;
    const start = Math.max(
      requestedStart ?? Number.NEGATIVE_INFINITY,
      workshiftStart ?? Number.NEGATIVE_INFINITY
    );
    const finish = Math.min(
      requestedFinish ?? Number.POSITIVE_INFINITY,
      workshiftFinish ?? Number.POSITIVE_INFINITY
    );
    const events = store
      .getState()
      .events.filter((event) => {
        const timestamp = Date.parse(event.timestamp);
        return (
          workshiftMatches &&
          timestamp >= start &&
          timestamp <= finish &&
          (!allowedTypes.length || allowedTypes.includes(event.type)) &&
          (!query || JSON.stringify(event).toLowerCase().includes(query))
        );
      })
      .map(eventView);
    return { eventList: page(events, variables) };
  },
};
